/**
 * Pie / donut chart component built with D3.js v7.
 *
 * Supports donut style with centre total label, arc tween entrance animation,
 * hover expansion, external labels for segments > 7%, and a legend.
 */

import { useRef, useEffect, useCallback, useState } from "react";
import * as d3 from "d3";
import type { PieChartConfig } from "../../types/index.js";
import { positionTooltip, formatNumber, useResizeObserver } from "./chartUtils.js";
import { useRenderLoopGuard } from "../../hooks/useRenderLoopGuard.js";

interface PieChartViewProps {
	config: PieChartConfig;
	isModal?: boolean;
}

export default function PieChartView({ config, isModal = false }: PieChartViewProps) {
	useRenderLoopGuard("PieChartView");
	const svgRef = useRef<SVGSVGElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const { width: containerWidth, height: containerHeight } = useResizeObserver(containerRef);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

	const { data, donut = false } = config;

	const renderChart = useCallback(() => {
		if (!svgRef.current || !data || data.length === 0 || containerWidth === 0) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();

		// Use container height only when explicitly in modal context (isModal=true).
		// For inline charts the container height is content-driven, so using it
		// would create a feedback loop: SVG grows → container grows → SVG grows…
		const effectiveHeight = isModal ? containerHeight : 0;
		const availableSize = effectiveHeight > 0 ? Math.min(containerWidth, effectiveHeight) : containerWidth;
		const maxSize = effectiveHeight > 0 ? Math.min(containerWidth, effectiveHeight) - 40 : 360;
		const size = Math.min(availableSize, maxSize);
		const cx = size / 2;
		const cy = size / 2;
		const outerR = (size / 2) - 20;
		const innerR = donut ? outerR * 0.6 : 0;
		const hoverOffset = 8;

		svg
		.attr("width", size)
		.attr("height", size)             // intrinsic size for SVG export
		.style("height", `${size}px`)     // CSS override: beats initial height:0 on SVG element
		.attr("role", "img");

		const g = svg.append("g").attr("transform", `translate(${cx},${cy})`);

		const total = d3.sum(data, (d) => d.value);

		const pie = d3
			.pie<(typeof data)[0]>()
			.value((d) => d.value)
			.sort(null);

		const arcGen = d3
			.arc<d3.PieArcDatum<(typeof data)[0]>>()
			.innerRadius(innerR)
			.outerRadius(outerR);

		const arcHover = d3
			.arc<d3.PieArcDatum<(typeof data)[0]>>()
			.innerRadius(innerR)
			.outerRadius(outerR + hoverOffset);

		const arcs = pie(data);

		// Arc paths
		const arcPaths = g
			.selectAll("path.arc-segment")
			.data(arcs)
			.join("path")
			.attr("class", "arc-segment")
			.attr("fill", (d) => d.data.colour)
			.attr("stroke", "#fff")
			.attr("stroke-width", 2)
			.style("cursor", "pointer")
			.on("mouseenter", function (event: MouseEvent, d) {
				setHoveredIndex(d.index);
				d3.select(this)
					.transition()
					.duration(150)
					.attrTween("d", function () {
						return () => arcHover(d) ?? "";
					});
				if (tooltipRef.current) {
					const pct = total > 0 ? ((d.data.value / total) * 100).toFixed(1) : "0.0";
					tooltipRef.current.innerHTML = `<strong>${d.data.name}</strong><br/>${formatNumber(d.data.value)} (${pct}%)`;
					tooltipRef.current.style.opacity = "1";
					positionTooltip(tooltipRef.current, event);
				}
			})
			.on("mousemove", function (event: MouseEvent) {
				if (tooltipRef.current) positionTooltip(tooltipRef.current, event);
			})
			.on("mouseleave", function (_, d) {
				setHoveredIndex(null);
				d3.select(this)
					.transition()
					.duration(150)
					.attrTween("d", function () {
						return () => arcGen(d) ?? "";
					});
				if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
			});

		// Arc tween entrance animation
		arcPaths
			.transition()
			.duration(700)
			.ease(d3.easeCubicOut)
			.attrTween("d", function (d) {
				const i = d3.interpolate({ startAngle: 0, endAngle: 0 }, d);
				return (t) => arcGen(i(t) as d3.PieArcDatum<(typeof data)[0]>) ?? "";
			});

		// Centre label (donut only)
		if (donut) {
			g.append("text")
				.attr("text-anchor", "middle")
				.attr("dominant-baseline", "middle")
				.attr("dy", "-0.2em")
				.attr("font-family", "var(--font-heading)")
				.attr("font-size", Math.min(22, outerR * 0.38) + "px")
				.attr("font-weight", "700")
				.attr("fill", "#1a1a2e")
				.text(formatNumber(total));

			g.append("text")
				.attr("text-anchor", "middle")
				.attr("dominant-baseline", "middle")
				.attr("dy", "1.1em")
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "11px")
				.attr("fill", "#6b7280")
				.text("Total");
		}

		// External labels for segments > 7%
		const labelArc = d3
			.arc<d3.PieArcDatum<(typeof data)[0]>>()
			.innerRadius(outerR + 12)
			.outerRadius(outerR + 12);

		arcs.forEach((d) => {
			const pct = total > 0 ? (d.data.value / total) * 100 : 0;
			if (pct < 7) return;

			const mid = (d.startAngle + d.endAngle) / 2;
			const [lx, ly] = labelArc.centroid(d);
			const isRight = mid < Math.PI;

			g.append("text")
				.attr("transform", `translate(${lx},${ly})`)
				.attr("text-anchor", isRight ? "start" : "end")
				.attr("dominant-baseline", "middle")
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "10px")
				.attr("fill", "#6b7280")
				.text(`${pct.toFixed(0)}%`);
		});
	}, [data, donut, containerWidth, containerHeight, isModal]);

	useEffect(() => {
		renderChart();
	}, [renderChart]);

	// Dim non-hovered arcs via CSS opacity (complement to D3 hover)
	useEffect(() => {
		if (!svgRef.current) return;
		const paths = svgRef.current.querySelectorAll("path.arc-segment");
		paths.forEach((p, i) => {
			(p as SVGElement).style.opacity =
				hoveredIndex === null || hoveredIndex === i ? "1" : "0.45";
		});
	}, [hoveredIndex]);

	if (!data || data.length === 0) {
		return (
			<div className="chart-empty-state" role="status">
				No data available
			</div>
		);
	}

	return (
		<div ref={containerRef} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", width: "100%", height: "100%" }}>
			{/* height: 0 prevents the browser default 150px SVG height before D3 renders.
			     D3's renderChart sets .style("height") to override this once dimensions are known. */}
			<svg ref={svgRef} style={{ display: "block", height: 0 }} />

			{/* Legend */}
			<div className="chart-legend" aria-label="Chart legend">
				{data.map((item) => (
					<div key={item.name} className="chart-legend-item">
						<span
							className="chart-legend-swatch"
							style={{ background: item.colour }}
							aria-hidden="true"
						/>
						<span className="chart-legend-label">{item.name}</span>
					</div>
				))}
			</div>

			{/* Tooltip */}
			<div
				ref={tooltipRef}
				className="chart-tooltip"
				role="tooltip"
				aria-live="polite"
				style={{ opacity: 0 }}
			/>
		</div>
	);
}
